// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Entregas.tsx
//  Página de Gerenciamento de Entregas
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Search, Package, Truck, CheckCircle2, Clock, XCircle,
  ChevronLeft, ChevronRight, MapPin, PackageCheck, ExternalLink,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { useEntrega } from '@/features/operacional/hooks/useEntrega';
import { calcularStatusEntrega } from '@/features/operacional/types';
import type { EntregaResumo, StatusEntrega } from '@/features/operacional/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { EntregaDetalheDialog } from './EntregaDetalheDialog';

// ── Helpers ───────────────────────────────────────────────────────────────

function buildMapsUrl(e: { endereco?: string | null; numero?: string | null; bairro?: string | null; cidade?: string | null; uf?: string | null }) {
  const parts = [e.endereco, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusEntrega, { label: string; className: string; icon: typeof Clock }> = {
  pendente:  { label: 'Pendente',    className: 'bg-blue-500/10 text-blue-500',     icon: Clock        },
  em_rota:   { label: 'Em Rota',     className: 'bg-yellow-500/10 text-yellow-500', icon: Truck        },
  entregue:  { label: 'Entregue',    className: 'bg-green-500/10 text-green-500',   icon: PackageCheck },
  cancelada: { label: 'Cancelada',   className: 'bg-red-500/10 text-red-500',       icon: XCircle      },
  retirada:  { label: 'Ret. na Loja', className: 'bg-purple-500/10 text-purple-500', icon: CheckCircle2 },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

// ── Componente de ação de status ──────────────────────────────────────────

interface StatusActionDialogProps {
  entrega: EntregaResumo | null;
  onClose: () => void;
  onConfirm: (request: { isAberta?: boolean; isFinalizado?: boolean; isCancelada?: boolean }) => void;
  isUpdating: boolean;
}

function StatusActionDialog({ entrega, onClose, onConfirm, isUpdating }: StatusActionDialogProps) {
  if (!entrega) return null;
  const status = calcularStatusEntrega(entrega);

  const acoes: Array<{ label: string; className?: string; request: Parameters<typeof onConfirm>[0] }> = [];

  if (status === 'pendente') {
    acoes.push({ label: 'Marcar como Em Rota', request: { isAberta: false } });
    acoes.push({ label: 'Cancelar Entrega', className: 'text-destructive', request: { isCancelada: true } });
  }
  if (status === 'em_rota') {
    acoes.push({ label: 'Confirmar Entrega', request: { isFinalizado: true } });
    acoes.push({ label: 'Cancelar Entrega', className: 'text-destructive', request: { isCancelada: true } });
  }

  return (
    <Dialog open={!!entrega} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Atualizar Status — Entrega #{entrega.numeroEntrega}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 text-sm text-muted-foreground">
          <p><strong>Cliente:</strong> {entrega.nomeRazao ?? '—'}</p>
          <p><strong>Data Entrega:</strong> {new Date(entrega.dtEntrega).toLocaleDateString('pt-BR')}</p>
          {entrega.endereco && (
            <p className="flex items-center gap-2 flex-wrap">
              <strong>Endereço:</strong>
              <span>{entrega.endereco}, {entrega.numero} — {entrega.bairro}, {entrega.cidade}/{entrega.uf}</span>
              <a
                href={buildMapsUrl(entrega)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
              >
                <ExternalLink className="h-3 w-3" />Ver no Maps
              </a>
            </p>
          )}
        </div>
        {acoes.length === 0 ? (
          <p className="text-muted-foreground text-sm py-2">
            Nenhuma ação disponível para o status atual.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {acoes.map((acao) => (
              <Button
                key={acao.label}
                variant="outline"
                className={acao.className}
                disabled={isUpdating}
                onClick={() => onConfirm(acao.request)}
              >
                {acao.label}
              </Button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const Entregas = () => {
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [actionEntrega, setActionEntrega] = useState<EntregaResumo | null>(null);
  const [detalheId,     setDetalheId]     = useState<number | null>(null);

  // Abre nos últimos 30 dias (pelo movimento), mais recentes primeiro.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'movimento' | 'entrega' | 'numero' | 'cliente' | 'valor'>('movimento', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const { entregas, total, totalPaginas, resumo, isLoading, isFetching, atualizarStatus, isUpdating } =
    useEntrega({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  // ── KPIs (do servidor: busca + período inteiros, não só a página) ──────

  const totalPendentes  = resumo?.pendentes       ?? 0;
  const totalEmRota     = resumo?.emRota          ?? 0;
  const totalEntregues  = resumo?.entregues       ?? 0;
  const valorTotalCarga = resumo?.valorTotalCarga ?? 0;

  // ── Paginação ──────────────────────────────────────────────────────────

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Gerenciamento de Entregas</h1>
          <p className="text-muted-foreground mt-1">Acompanhe e atualize o status das entregas</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalPendentes}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando saída</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Truck className="h-4 w-4" />Em Rota
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{totalEmRota}</div>
            <p className="text-xs text-muted-foreground mt-1">Saíram para entrega</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />Entregues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalEntregues}</div>
            <p className="text-xs text-muted-foreground mt-1">Concluídas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />Valor Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-foreground">
              {valorTotalCarga.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">em carga (sem as canceladas)</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Entregas</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº, cliente ou motorista..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Movimento" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="em_rota">Em Rota</SelectItem>
                  <SelectItem value="entregue">Entregue</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                  <SelectItem value="retirada">Ret. na Loja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº Entrega</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="cliente" ordenacao={ordenacao} decrescentePadrao={false}>Cliente</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="movimento" ordenacao={ordenacao}>Movimento</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="entrega" ordenacao={ordenacao}>Dt. Entrega</CabecalhoOrdenavel>
                  <TableHead>Endereço</TableHead>
                  <TableHead>Motorista</TableHead>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor Carga</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : entregas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo !== 'todos'
                        ? 'Nenhuma entrega neste período — amplie o período para ver as anteriores'
                        : 'Nenhuma entrega encontrada'}
                    </TableCell>
                  </TableRow>
                ) : (
                  entregas.map((e: EntregaResumo) => {
                    const status = calcularStatusEntrega(e);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeAtualizar = status === 'pendente' || status === 'em_rota';
                    return (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium">#{e.numeroEntrega}</TableCell>
                        <TableCell>
                          {e.nomeRazao || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>{new Date(e.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          {new Date(e.dtEntrega).toLocaleDateString('pt-BR')}
                          {e.horaEntrega && ` ${e.horaEntrega}`}
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate">
                          {e.isRetirarLoja ? (
                            <span className="text-purple-600 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />Retirada na loja
                            </span>
                          ) : e.endereco ? (
                            `${e.endereco}, ${e.numero ?? ''} — ${e.bairro ?? ''}, ${e.cidade ?? ''}/${e.uf ?? ''}`
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {e.motorista || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {(e.valorTotalCarga ?? 0).toLocaleString('pt-BR', {
                            style: 'currency', currency: 'BRL',
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {cfg.label}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {!e.isRetirarLoja && e.endereco && (
                              <Button
                                size="sm" variant="ghost"
                                title="Ver no Google Maps"
                                asChild
                              >
                                <a href={buildMapsUrl(e)} target="_blank" rel="noopener noreferrer">
                                  <MapPin className="h-4 w-4 text-primary" />
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              title="Ver detalhes"
                              onClick={() => setDetalheId(e.id)}
                            >
                              Detalhes
                            </Button>
                            {podeAtualizar && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setActionEntrega(e)}
                              >
                                Atualizar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Rodapé de paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && (
                <span className="ml-2 text-xs">(atualizando...)</span>
              )}
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  Itens por página:
                </span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(1)}
                  disabled={pagina === 1}
                >
                  «
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p - 1)}
                  disabled={pagina === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button
                      key={page}
                      variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs"
                      onClick={() => setPagina(page)}
                    >
                      {page}
                    </Button>
                  );
                })}

                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p + 1)}
                  disabled={pagina >= totalPaginas}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(totalPaginas)}
                  disabled={pagina >= totalPaginas}
                >
                  »
                </Button>
              </div>

              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Página {pagina} de {totalPaginas}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de atualização de status */}
      <StatusActionDialog
        entrega={actionEntrega}
        onClose={() => setActionEntrega(null)}
        isUpdating={isUpdating}
        onConfirm={(request) => {
          if (actionEntrega) {
            atualizarStatus(
              { id: actionEntrega.id, data: request },
              { onSuccess: () => setActionEntrega(null) }
            );
          }
        }}
      />

      {/* Dialog de detalhe da entrega */}
      <EntregaDetalheDialog
        entregaId={detalheId}
        onClose={() => setDetalheId(null)}
      />
    </div>
  );
};

export default Entregas;
